import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import FormInput from "../../molecules/FormInput";
import { FormEvent, useState } from "react";
import UploadDialog from "../UploadDialog";
import AttachmentList from "../../molecules/AttachmentList";
import useConfirm from "../../../hooks/useConfirm";
import useRefresh from "../../../hooks/useRefresh";
import { TicketService } from "../../../services/api";
import { useParams } from "react-router";
import Authorized from "../../atoms/Authorized";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import useBreakpoint from "../../../hooks/useBreakpoint";

interface CreateTicketResponseProps {
  ticketId: string;
}

export default function CreateTicketResponse(props: CreateTicketResponseProps) {
  const { t } = useTranslation("Ticket");
  const { _id } = useParams();
  const [open, setOpen] = useState(false);
  const [internalResponse, setInternalResponse] = useState(false);
  const [response, setResponse] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const confirm = useConfirm();
  const refresh = useRefresh();
  const breakpoint = useBreakpoint();

  const handleChangeFiles = (newFiles: File[]) => {
    setFiles(newFiles);
    setOpen(false);
  };

  const handleRespond = async () => {
    if (!_id) return;
    if (files.length === 0 && !response)
      return toast(t("ResponseEmpty"), { type: "warning" });
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }
    const res = await TicketService.createTicketResponse(
      {
        ticketId: _id,
        content: response,
        internalResponse: internalResponse,
      },
      { data: formData }
    );
    if (res?.data?.success) {
      refresh();
      setResponse("");
      setInternalResponse(false);
      setFiles([]);
    }
  };

  const handleClickRespond = (e: FormEvent) => {
    e.preventDefault();
    confirm(handleRespond);
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "background.default",
        borderRadius: (theme) => (theme.shape.borderRadius as number) / 8,
      }}
      component="form"
      onSubmit={handleClickRespond}
    >
      <UploadDialog
        open={open}
        onCancel={() => setOpen(false)}
        onSubmit={handleChangeFiles}
      />
      <Stack spacing={2}>
        <Authorized role="user">
          <FormInput
            field="response"
            label={""}
            onChange={(v) => setResponse(v as string)}
            type="string"
            value={response}
            multiline
            minRows={4}
            placeholder={t("ResponsePlaceholder")}
          />
          <Stack spacing={1}>
            <Divider />
            <Stack spacing={2} mt={2}>
              {files.length > 0 && (
                <Box position="relative">
                  <AttachmentList
                    ticketId={props.ticketId}
                    upload
                    files={files}
                  />
                  <IconButton
                    color="error"
                    onClick={() => {
                      setFiles([]);
                    }}
                    size="medium"
                    sx={{
                      p: 0.25,
                      position: "absolute",
                      right: 8,
                      top: 8,
                      zIndex: 1,
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              )}

              <Stack spacing={"auto"} direction={"row"}>
                <Box>
                  <Authorized role="agent">
                    <FormInput
                      label={t("PrivateResponse")}
                      field="privateResponse"
                      type="switch"
                      value={internalResponse}
                      onChange={(v) => setInternalResponse(v as boolean)}
                    />
                  </Authorized>
                </Box>

                <Stack spacing={1} direction={"row"}>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setOpen(true);
                    }}
                    size="medium"
                  >
                    <AttachFileIcon />
                  </IconButton>

                  <Button
                    startIcon={<SendIcon />}
                    variant="contained"
                    color="success"
                    type="submit"
                    size={breakpoint.sm ? "medium" : "small"}
                  >
                    {t("Send")}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Authorized>
      </Stack>
    </Box>
  );
}
